import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { FilterOptions, ProjectsByLpaResponse } from '../../models/projects-by-lpa.models';

@Injectable({
  providedIn: 'root'
})
export class ProjectsByLpaService {
  private apiUrl = `${environment.main_url}/agilelegal/reports/tasks/projects-by-lpa`;
  private practiceAreasUrl = `${environment.main_url}/agilelegal/practiceArea`;
  private organizationsUrl = `${environment.main_url}/agilelegal/organizations/names`;
  private mattersUrl = `${environment.main_url}/agilelegal/matters/names`;
  private projectTypesUrl = `${environment.main_url}/agilelegal/category/projectTypes`;

  constructor(private http: HttpClient) { }

  getProjectsByLpaData(filters: FilterOptions): Observable<ProjectsByLpaResponse> {
    let params = new HttpParams();
    
    // Add all filter parameters to the query, only if they have actual values
    if (filters.startDate && filters.startDate !== '') {
      params = params.set('startDate', filters.startDate);
    }
    if (filters.endDate && filters.endDate !== '') {
      params = params.set('endDate', filters.endDate);
    }
    if (filters.organization && filters.organization !== '') {
      params = params.set('organization', filters.organization);
    }
    if (filters.matter && filters.matter !== '') {
      params = params.set('matter', filters.matter);
    }
    if (filters.projectType && filters.projectType !== '') {
      params = params.set('projectTypeCode', filters.projectType);
    }
    
    return this.http.get<ProjectsByLpaResponse>(this.apiUrl, { params })
      .pipe(
        catchError(error => {
          console.error('Error fetching projects by LPC data:', error);
          return of({ 
            data: [], 
            message: 'Failed to fetch projects by LPC data',
            customValues: null
          });
        })
      );
  }
  
  getPracticeAreas(): Observable<any> {
    return this.http.get<any>(this.practiceAreasUrl)
      .pipe(
        catchError(error => {
          console.error('Error fetching practice areas:', error);
          return of({ 
            data: [], 
            message: 'Failed to fetch practice areas',
            customValues: null
          });
        })
      );
  }

  getOrganizationNames(): Observable<any> {
    return this.http.get<any>(this.organizationsUrl)
      .pipe(
        map(response => {
          // Transform data for ng-select format (id/name pairs)
          if (response && response.data && Array.isArray(response.data)) {
            return {
              data: response.data.map((name: string) => ({
                id: name,
                name: name
              })),
              message: response.message,
              customValues: response.customValues
            };
          }
          return response;
        }),
        catchError(error => {
          console.error('Error fetching organization names:', error);
          return of({ 
            data: [], 
            message: 'Failed to fetch organization names',
            customValues: null
          });
        })
      );
  }

  getMatterNames(organization?: string): Observable<any> {
    let url = this.mattersUrl;
    
    // If organization is provided, add it as a query parameter
    if (organization) {
      url = `${this.mattersUrl}?organization=${encodeURIComponent(organization)}`;
    }
    
    return this.http.get<any>(url)
      .pipe(
        map(response => {
          // Transform data for ng-select format (id/name pairs)
          if (response && response.data && Array.isArray(response.data)) {
            return {
              data: response.data.map((name: string) => ({
                id: name,
                name: name
              })),
              message: response.message,
              customValues: response.customValues
            };
          }
          return response;
        }),
        catchError(error => {
          console.error('Error fetching matter names:', error);
          return of({ 
            data: [], 
            message: 'Failed to fetch matter names',
            customValues: null
          });
        })
      );
  }
  
  getProjectTypes(): Observable<any> {
    return this.http.get<any>(this.projectTypesUrl)
      .pipe(
        map(response => {
          // Transform data for ng-select format (id/name pairs)
          if (response && response.data && Array.isArray(response.data)) {
            return {
              data: response.data.map((type: any) => ({
                id: type.code,  // Using code as the ID value for the payload
                name: type.description  // Using description as the display name
              })),
              message: response.message,
              customValues: response.customValues
            };
          }
          return response;
        }),
        catchError(error => {
          console.error('Error fetching project types:', error);
          return of({ 
            data: [], 
            message: 'Failed to fetch project types',
            customValues: null
          });
        })
      );
  }
}
